import { useMutation } from "@tanstack/react-query";
import { useLingui } from "@lingui/react";
import { toast } from "sonner";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { errorToastText } from "@/lib/translateBackendError";

/**
 * Downloads for the two printable settling-in documents (issue #387): the
 * Eingewöhnungsplan a parent is handed, and the progress summary a parent
 * meeting is held over.
 *
 * Both endpoints stream `application/pdf` rather than JSON, so they use a raw
 * `fetch` with a bearer token instead of the generated OpenAPI client — the
 * same shape `useDownloadInvoicePDF` uses.
 */

/** The two documents, matching the two route segments. */
export type SettlingInPdfKind = "schedule" | "progress";

export interface DownloadSettlingInPdfParams {
  planId: string;
  /** Language code (de, en, fr, it, es, pt). The backend falls back to German. */
  lang?: string;
}

interface DownloadedPdf {
  blob: Blob;
  filename: string;
}

async function downloadSettlingInPdf(
  { planId, kind, lang }: DownloadSettlingInPdfParams & { kind: SettlingInPdfKind }
): Promise<DownloadedPdf> {
  const { getAuthToken } = await import("@/api/auth-helper");
  const token = await getAuthToken();
  const baseUrl = import.meta.env.VITE_API_BASE || "";
  const query = lang ? `?lang=${encodeURIComponent(lang)}` : "";

  const response = await fetch(
    `${baseUrl}/api/v1/settling-in/${planId}/${kind}/pdf${query}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    // Keep status and body so errorToastText can read the code and message.
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status, body });
  }

  const blob = await response.blob();
  // The backend names the file (child slug + start date); the fallback only
  // covers a proxy that strips the header.
  const disposition = response.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || `settling-in-${kind}-${planId}.pdf`;

  return { blob, filename };
}

/** Saves the blob under its filename via a transient anchor click. */
function saveBlob({ blob, filename }: DownloadedPdf): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

function successMessage(i18n: I18n): string {
  return i18n._({ id: "settlingIn.toast.pdfDownloadSuccess", message: "PDF downloaded successfully" });
}

const pdfDownloadError = msg({
  id: "settlingIn.toast.pdfDownloadError",
  message: "The PDF could not be downloaded. Please try again.",
});

function errorMessage(i18n: I18n, error: unknown): string {
  return errorToastText(error, i18n, pdfDownloadError);
}

/** Downloads the Eingewöhnungsplan (schedule) PDF for a settling-in plan. */
export function useDownloadSettlingInSchedulePDF() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (params: DownloadSettlingInPdfParams) =>
      downloadSettlingInPdf({ ...params, kind: "schedule" }),
    onSuccess: (result) => {
      saveBlob(result);
      toast.success(successMessage(i18n));
    },
    onError: (error) => {
      toast.error(errorMessage(i18n, error));
    },
  });
}

/** Downloads the progress summary PDF for a settling-in plan. */
export function useDownloadSettlingInProgressPDF() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (params: DownloadSettlingInPdfParams) =>
      downloadSettlingInPdf({ ...params, kind: "progress" }),
    onSuccess: (result) => {
      saveBlob(result);
      toast.success(successMessage(i18n));
    },
    onError: (error) => {
      toast.error(errorMessage(i18n, error));
    },
  });
}
